import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { de as deLocale } from 'date-fns/locale';
import { format, addDays, parseISO } from 'date-fns';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import LinkIcon from '@mui/icons-material/Link';
import KeyIcon from '@mui/icons-material/Key';
import DeleteIcon from '@mui/icons-material/Delete';

import { useNavigate } from 'react-router-dom';
import { navigableProps } from '../utils/navigation';
import PageHeader from '../components/common/PageHeader';
import ConfirmDialog from '../components/common/ConfirmDialog';
import TableSkeleton from '../components/common/TableSkeleton';
import { useNotification } from '../hooks/NotificationContext';
import { usePermissions } from '../hooks/usePermissions';
import * as P from '../constants/permissions';
import {
  uploadSessionsService,
  UploadSessionListItem,
  UploadSessionDetail,
} from '../services/uploadSessionsService';

const STATUS_CONFIG: Record<string, { label: string; color: 'default' | 'success' | 'warning' | 'error' }> = {
  neu: { label: 'Neu', color: 'default' },
  hochgeladen: { label: 'Hochgeladen', color: 'success' },
  abgelaufen: { label: 'Abgelaufen', color: 'error' },
};

const UploadSessionsListPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { showSuccess, showError } = useNotification();
  const { hasPermission } = usePermissions();
  const canManage = hasPermission(P.UPLOAD_SESSIONS_MANAGE);

  const [sessions, setSessions] = useState<UploadSessionListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [successDialog, setSuccessDialog] = useState<UploadSessionDetail | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<UploadSessionListItem | null>(null);

  const [beschreibung, setBeschreibung] = useState('');
  const [gueltigBis, setGueltigBis] = useState<Date | null>(addDays(new Date(), 14));
  const [creating, setCreating] = useState(false);

  const loadSessions = useCallback(async () => {
    try {
      setLoading(true);
      const data = await uploadSessionsService.list();
      setSessions(data);
    } catch {
      showError('Fehler beim Laden der Upload-Sessions');
    } finally {
      setLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    loadSessions();
  }, [loadSessions]);

  const getPublicLink = (token: string) => `${window.location.origin}/upload/${token}`;

  const copyToClipboard = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      showSuccess(`${label} in die Zwischenablage kopiert`);
    } catch {
      showError('Kopieren fehlgeschlagen');
    }
  };

  const handleCreate = async () => {
    if (!beschreibung.trim() || !gueltigBis) return;
    try {
      setCreating(true);
      const result = await uploadSessionsService.create({
        beschreibung: beschreibung.trim(),
        gueltig_bis: format(gueltigBis, 'yyyy-MM-dd'),
      });
      setCreateOpen(false);
      setBeschreibung('');
      setGueltigBis(addDays(new Date(), 14));
      setSuccessDialog(result);
      loadSessions();
    } catch {
      showError('Fehler beim Anlegen der Session');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await uploadSessionsService.delete(deleteTarget.id);
      showSuccess('Upload-Session gelöscht');
      setDeleteTarget(null);
      loadSessions();
    } catch {
      showError('Fehler beim Löschen');
    }
  };

  const formatDate = (iso: string) => format(parseISO(iso), 'dd.MM.yyyy');
  const formatDateTime = (iso: string) => format(parseISO(iso), 'dd.MM.yyyy HH:mm');

  return (
    <Box>
      <PageHeader
        title="Kundenuploads"
        subtitle="Upload-Sessions für externe Dateiübermittlung"
        onAdd={canManage ? () => setCreateOpen(true) : undefined}
        addLabel="Neue Session"
      />

      {loading ? (
        <TableSkeleton rows={5} columns={7} />
      ) : sessions.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">Keine Upload-Sessions vorhanden</Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Beschreibung</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Gültig bis</TableCell>
                <TableCell>Erstellt</TableCell>
                <TableCell align="center">Dateien</TableCell>
                <TableCell align="right">Aktionen</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sessions.map((s) => {
                const cfg = STATUS_CONFIG[s.status] || STATUS_CONFIG.neu;
                return (
                  <TableRow
                    key={s.id}
                    hover
                    {...navigableProps(navigate, `/kundenuploads/${s.id}`)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {s.beschreibung}
                      </Typography>
                      {s.erstellt_von && (
                        <Typography variant="caption" color="text.secondary">
                          von {s.erstellt_von.vorname} {s.erstellt_von.nachname}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip label={cfg.label} color={cfg.color} size="small" />
                    </TableCell>
                    <TableCell>{formatDate(s.gueltig_bis)}</TableCell>
                    <TableCell>{formatDateTime(s.erstellt_am)}</TableCell>
                    <TableCell align="center">
                      <Chip label={s.datei_anzahl} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                        <Tooltip title="Link kopieren">
                          <IconButton size="small" onClick={() => copyToClipboard(getPublicLink(s.token), 'Link')}>
                            <LinkIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Passwort kopieren">
                          <IconButton size="small" onClick={() => copyToClipboard(s.secret, 'Passwort')}>
                            <KeyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {canManage && (
                          <Tooltip title="Löschen">
                            <IconButton size="small" onClick={() => setDeleteTarget(s)} color="error">
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Create Dialog */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Neue Upload-Session</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: '16px !important' }}>
          <TextField
            label="Beschreibung"
            value={beschreibung}
            onChange={(e) => setBeschreibung(e.target.value)}
            fullWidth
            multiline
            minRows={2}
            placeholder="z.B. Dokumentation Pentest Q1 2026"
          />
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={deLocale}>
            <DatePicker
              label="Gültig bis"
              value={gueltigBis}
              onChange={(date) => setGueltigBis(date)}
              minDate={addDays(new Date(), 1)}
              format="dd.MM.yyyy"
              slotProps={{ textField: { fullWidth: true } }}
            />
          </LocalizationProvider>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setCreateOpen(false)} sx={{ color: 'text.secondary' }}>
            Abbrechen
          </Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={creating || !beschreibung.trim() || !gueltigBis}
          >
            {creating ? 'Wird erstellt...' : 'Erstellen'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Dialog */}
      <Dialog open={!!successDialog} onClose={() => setSuccessDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Session erstellt</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
          <Typography variant="body2" color="text.secondary">
            Teilen Sie den Link und das Passwort separat mit dem Kunden.
          </Typography>
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
              Upload-Link
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
              <TextField
                value={successDialog ? getPublicLink(successDialog.token) : ''}
                fullWidth
                size="small"
                InputProps={{ readOnly: true }}
              />
              <Tooltip title="Kopieren">
                <IconButton
                  onClick={() => successDialog && copyToClipboard(getPublicLink(successDialog.token), 'Link')}
                >
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
              Passwort
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
              <TextField
                value={successDialog?.secret || ''}
                fullWidth
                size="small"
                InputProps={{ readOnly: true }}
              />
              <Tooltip title="Kopieren">
                <IconButton onClick={() => successDialog && copyToClipboard(successDialog.secret, 'Passwort')}>
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="contained" onClick={() => setSuccessDialog(null)}>
            Schließen
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirm */}
      <ConfirmDialog
        open={!!deleteTarget}
        title="Upload-Session löschen"
        message={`Möchten Sie die Session "${deleteTarget?.beschreibung}" wirklich löschen? Alle hochgeladenen Dateien werden ebenfalls entfernt.`}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </Box>
  );
};

export default UploadSessionsListPage;
